// XDF/ADX password-protected file unlocker.
// Algorithm recovered from the reference "XDF ADX Unlocker" (Windows CryptoAPI):
//   strip 0x108-byte header -> RC4 decrypt with key = MD5("$9Ad0$!#*)1QqPow^") -> normalize <flags> to 0x1
// Verified byte-for-byte against the reference locked/unlock pair.

const UNLOCK_PASSWORD = "$9Ad0$!#*)1QqPow^";
const HEADER_LEN = 0x108; // 264

// Candidate passwords tried when auto-unlocking a protected XDF (RC4/MD5 format).
export const DEFAULT_PASSWORDS = [
  UNLOCK_PASSWORD, "", ".", " ", "0", "1", "x", "-", "00000000",
  "tunerpro", "TunerPro", "TUNERPRO", "tirotuner", "tiro",
  "admin", "1234", "12345", "123456", "password", "honda", "ecu",
  "brm016", "power61",
];

// ---- Learned passwords (persisted) --------------------------------------------------
// When the user enters a password (or the file's real open/modify password) that
// successfully unlocks an XDF/ADX, we remember it so future files locked with the SAME
// password or algorithm auto-unlock with no input. Stored in localStorage, deduped.
const LEARNED_KEY = "tiro_xdf_learned_pw";

export function getLearnedPasswords() {
  try {
    const raw = localStorage.getItem(LEARNED_KEY);
    const arr = raw ? JSON.parse(raw) : [];
    return Array.isArray(arr) ? arr.filter((p) => typeof p === "string") : [];
  } catch { return []; }
}

export function rememberXdfPassword(...pws) {
  try {
    const cur = getLearnedPasswords();
    let changed = false;
    for (const pw of pws) {
      if (typeof pw !== "string" || pw === "") continue;
      if (DEFAULT_PASSWORDS.includes(pw) || cur.includes(pw)) continue;
      cur.push(pw); changed = true;
    }
    if (changed) localStorage.setItem(LEARNED_KEY, JSON.stringify(cur.slice(-200)));
    return changed;
  } catch { return false; }
}

// Full candidate list = caller extras + learned (persisted) + built-in defaults, deduped.
export function xdfCandidatePasswords(extra = []) {
  const out = [];
  for (const p of [...(extra || []), ...getLearnedPasswords(), ...DEFAULT_PASSWORDS]) {
    if (p !== undefined && p !== null && !out.includes(p)) out.push(p);
  }
  return out;
}

// Heuristic: is this XDF payload encrypted (binary) rather than plain XML?
export function looksEncryptedXdf(text) {
  if (!text) return false;
  const head = text.slice(0, 600);
  if (head.includes("XDFFORMAT") || head.includes("ADXFORMAT")) return false;
  return text.charCodeAt(0) === 0x05 || /[\x00-\x08\x0e-\x1f]/.test(head) || !head.replace(/^\uFEFF/, "").trimStart().startsWith("<");
}

const MD5_K = [
  0xd76aa478, 0xe8c7b756, 0x242070db, 0xc1bdceee, 0xf57c0faf, 0x4787c62a, 0xa8304613, 0xfd469501,
  0x698098d8, 0x8b44f7af, 0xffff5bb1, 0x895cd7be, 0x6b901122, 0xfd987193, 0xa679438e, 0x49b40821,
  0xf61e2562, 0xc040b340, 0x265e5a51, 0xe9b6c7aa, 0xd62f105d, 0x02441453, 0xd8a1e681, 0xe7d3fbc8,
  0x21e1cde6, 0xc33707d6, 0xf4d50d87, 0x455a14ed, 0xa9e3e905, 0xfcefa3f8, 0x676f02d9, 0x8d2a4c8a,
  0xfffa3942, 0x8771f681, 0x6d9d6122, 0xfde5380c, 0xa4beea44, 0x4bdecfa9, 0xf6bb4b60, 0xbebfbc70,
  0x289b7ec6, 0xeaa127fa, 0xd4ef3085, 0x04881d05, 0xd9d4d039, 0xe6db99e5, 0x1fa27cf8, 0xc4ac5665,
  0xf4292244, 0x432aff97, 0xab9423a7, 0xfc93a039, 0x655b59c3, 0x8f0ccc92, 0xffeff47d, 0x85845dd1,
  0x6fa87e4f, 0xfe2ce6e0, 0xa3014314, 0x4e0811a1, 0xf7537e82, 0xbd3af235, 0x2ad7d2bb, 0xeb86d391,
];
const MD5_S = [
  7, 12, 17, 22, 7, 12, 17, 22, 7, 12, 17, 22, 7, 12, 17, 22,
  5, 9, 14, 20, 5, 9, 14, 20, 5, 9, 14, 20, 5, 9, 14, 20,
  4, 11, 16, 23, 4, 11, 16, 23, 4, 11, 16, 23, 4, 11, 16, 23,
  6, 10, 15, 21, 6, 10, 15, 21, 6, 10, 15, 21, 6, 10, 15, 21,
];

export function md5Bytes(input) {
  const rl = (x, c) => (x << c) | (x >>> (32 - c));
  const msgLen = input.length;
  const total = (((msgLen + 8) >>> 6) + 1) << 6;
  const bytes = new Uint8Array(total);
  bytes.set(input);
  bytes[msgLen] = 0x80;
  const bitLen = msgLen * 8;
  bytes[total - 8] = bitLen & 0xff;
  bytes[total - 7] = (bitLen >>> 8) & 0xff;
  bytes[total - 6] = (bitLen >>> 16) & 0xff;
  bytes[total - 5] = (bitLen >>> 24) & 0xff;

  let a0 = 0x67452301, b0 = 0xefcdab89, c0 = 0x98badcfe, d0 = 0x10325476;
  const M = new Array(16);
  for (let off = 0; off < total; off += 64) {
    for (let i = 0; i < 16; i++) {
      const j = off + i * 4;
      M[i] = (bytes[j] | (bytes[j + 1] << 8) | (bytes[j + 2] << 16) | (bytes[j + 3] << 24)) >>> 0;
    }
    let A = a0, B = b0, C = c0, D = d0;
    for (let i = 0; i < 64; i++) {
      let F, g;
      if (i < 16) { F = (B & C) | (~B & D); g = i; }
      else if (i < 32) { F = (D & B) | (~D & C); g = (5 * i + 1) & 15; }
      else if (i < 48) { F = B ^ C ^ D; g = (3 * i + 5) & 15; }
      else { F = C ^ (B | ~D); g = (7 * i) & 15; }
      F = (F + A + MD5_K[i] + M[g]) | 0;
      A = D; D = C; C = B;
      B = (B + rl(F >>> 0, MD5_S[i])) | 0;
    }
    a0 = (a0 + A) | 0; b0 = (b0 + B) | 0; c0 = (c0 + C) | 0; d0 = (d0 + D) | 0;
  }
  const out = new Uint8Array(16);
  [a0, b0, c0, d0].forEach((v, idx) => {
    out[idx * 4] = v & 0xff;
    out[idx * 4 + 1] = (v >>> 8) & 0xff;
    out[idx * 4 + 2] = (v >>> 16) & 0xff;
    out[idx * 4 + 3] = (v >>> 24) & 0xff;
  });
  return out;
}

export function rc4(key, data) {
  const S = new Uint8Array(256);
  for (let i = 0; i < 256; i++) S[i] = i;
  let j = 0;
  for (let i = 0; i < 256; i++) {
    j = (j + S[i] + key[i % key.length]) & 255;
    const t = S[i]; S[i] = S[j]; S[j] = t;
  }
  const out = new Uint8Array(data.length);
  let a = 0, b = 0;
  for (let k = 0; k < data.length; k++) {
    a = (a + 1) & 255;
    b = (b + S[a]) & 255;
    const t = S[a]; S[a] = S[b]; S[b] = t;
    out[k] = data[k] ^ S[(S[a] + S[b]) & 255];
  }
  return out;
}

function latin1(bytes) {
  let s = "";
  const CH = 0x8000;
  for (let i = 0; i < bytes.length; i += CH) {
    s += String.fromCharCode.apply(null, bytes.subarray(i, Math.min(i + CH, bytes.length)));
  }
  return s;
}

export function bytesFromLatin1(str) {
  const out = new Uint8Array(str.length);
  for (let i = 0; i < str.length; i++) out[i] = str.charCodeAt(i) & 0xff;
  return out;
}

// Attempt to decrypt a protected XDF/ADX using candidate passwords.
// key = MD5(open_password); strip 264-byte header; RC4. Returns {success, text, ..., passwordUsed}.
export function unlockXdf(bytes, passwords, onLog) {
  const log = onLog || (() => {});
  const cands = [];
  for (const p of (passwords || [])) {
    if (p !== undefined && p !== null && !cands.includes(p)) cands.push(p);
  }
  if (!cands.includes("")) cands.push("");
  log(`Provider: Microsoft Enhanced Cryptographic Provider v1.0 (MD5 + RC4 128-bit)`);
  const offsets = [HEADER_LEN, 0, 4, 128, 131, 132, 130];
  for (const pw of cands) {
    const key = md5Bytes(bytesFromLatin1(pw));
    const keyHex = Array.from(key).map((b) => b.toString(16).padStart(2, "0")).join("");
    log(`Trying password "${pw}" → MD5 key ${keyHex}`);
    for (const off of offsets) {
      if (off >= bytes.length) continue;
      // fast detection on a small slice first
      const probe = latin1(rc4(key, bytes.subarray(off, off + 256)));
      if (probe.includes("XDFFORMAT") || probe.includes("ADXFORMAT") || probe.startsWith("<!-- ")) {
        const dec = rc4(key, bytes.subarray(off));
        const text = latin1(dec);
        if (text.includes("XDFFORMAT") || text.includes("ADXFORMAT")) {
          log(`✔ Decrypted — password "${pw}", header ${off} (0x${off.toString(16).toUpperCase()})`, "ok");
          const open = (/<openpassword>([\s\S]*?)<\/openpassword>/.exec(text) || [])[1] || "";
          const modify = (/<modifypassword>([\s\S]*?)<\/modifypassword>/.exec(text) || [])[1] || "";
          if (open) log(`Open password in file: ${open}`);
          if (modify) log(`Modify password in file: ${modify}`);
          const outText = text
            .replace(/[ \t]*<openpassword>[\s\S]*?<\/openpassword>\r?\n?/g, "")
            .replace(/[ \t]*<modifypassword>[\s\S]*?<\/modifypassword>\r?\n?/g, "")
            .replace(/<flags>0x[0-9A-Fa-f]+<\/flags>/g, "<flags>0x0</flags>");
          if (open) log(`Removed <openpassword> tag`, "ok");
          if (modify) log(`Removed <modifypassword> (XDF edit password "${modify}")`, "ok");
          log(`Set <flags> → 0x0 (all XDF passwords removed)`, "ok");
          // Remember the working password + the file's real open/modify password so future
          // files with the same password/algorithm auto-unlock with no input.
          try { if (rememberXdfPassword(pw, open, modify)) log(`Remembered password for future auto-unlock`, "ok"); } catch (_) {}
          return { success: true, text: outText, rawText: text, openPassword: open, modifyPassword: modify, offsetUsed: off, passwordUsed: pw, keyHex };
        }
      }
    }
  }
  log(`✖ No candidate password worked — enter the correct open password and retry`, "error");
  return { success: false };
}

